import React, { useState, useEffect } from 'react';
import { useContent } from '../../../hooks/useContent';
import { useToast } from '../../../hooks/useToast';
import type { NavLink } from '../../../types';
import { produce } from "immer";
import { XIcon } from '../../icons/XIcon';

const ArrowUpIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
);
  
const ArrowDownIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
);
  
interface NavLinkEditorProps {
    link: NavLink;
    path: number[];
    navType: 'header' | 'footer';
    index: number;
    siblingsCount: number;
    onUpdate: (navType: 'header' | 'footer', path: number[], field: keyof NavLink, value: any) => void;
    onRemove: (navType: 'header' | 'footer', path: number[]) => void;
    onAddChild: (navType: 'header' | 'footer', path: number[]) => void;
    onMove: (navType: 'header' | 'footer', path: number[], direction: 'up' | 'down') => void;
}
  
const NavLinkEditor: React.FC<NavLinkEditorProps> = ({ 
    link, path, navType, index, siblingsCount, onUpdate, onRemove, onAddChild, onMove 
}) => {
    return (
        <div className="space-y-2" style={{ paddingLeft: path.length > 1 ? '1.5rem' : '0' }}>
            <div className="grid grid-cols-12 gap-2 items-center p-2 bg-gray-100 rounded">
                <div className="col-span-1 flex items-center">
                    {path.length > 1 && <span className="text-gray-400 mr-2">↳</span>}
                </div>
                <input
                    type="text"
                    value={link.name}
                    onChange={e => onUpdate(navType, path, 'name', e.target.value)}
                    placeholder="Link Name"
                    className="col-span-3 px-2 py-1 border rounded-md"
                />
                <input
                    type="text"
                    value={link.path}
                    onChange={e => onUpdate(navType, path, 'path', e.target.value)}
                    placeholder="/path-or-url"
                    className="col-span-3 px-2 py-1 border rounded-md"
                />
                <select
                    value={link.target || '_self'}
                    onChange={e => onUpdate(navType, path, 'target', e.target.value)}
                    className="col-span-1 px-2 py-1 border rounded-md bg-white"
                >
                    <option value="_self">Self</option>
                    <option value="_blank">New Tab</option>
                </select>
                <div className="col-span-4 flex items-center justify-end gap-1">
                    <button onClick={() => onMove(navType, path, 'up')} disabled={index === 0} className="text-gray-500 hover:text-gray-800 p-1 disabled:opacity-30 disabled:cursor-not-allowed" title="Move Up">
                        <ArrowUpIcon/>
                    </button>
                    <button onClick={() => onMove(navType, path, 'down')} disabled={index >= siblingsCount - 1} className="text-gray-500 hover:text-gray-800 p-1 disabled:opacity-30 disabled:cursor-not-allowed" title="Move Down">
                        <ArrowDownIcon/>
                    </button>
                    <div className="w-px h-5 bg-gray-300 mx-1"></div>
                    <button onClick={() => onAddChild(navType, path)} className="text-blue-500 hover:text-blue-700 p-1" title="Add Sub-item">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                    </button>
                    <button onClick={() => onRemove(navType, path)} className="text-red-500 hover:text-red-700 p-1" title="Remove Item">
                        <XIcon className="h-4 w-4"/>
                    </button>
                </div>
            </div>
            {link.children?.map((child, childIndex) => (
                <NavLinkEditor
                    key={child.id || childIndex}
                    link={child}
                    path={[...path, childIndex]}
                    navType={navType}
                    index={childIndex}
                    siblingsCount={link.children!.length}
                    onUpdate={onUpdate}
                    onRemove={onRemove}
                    onAddChild={onAddChild}
                    onMove={onMove}
                />
            ))}
        </div>
    );
};


export const ManageNavigation: React.FC = () => {
    const { headerNav, footerNav, updateNavigation } = useContent();
    const { showToast } = useToast();

    const [editableHeaderNav, setEditableHeaderNav] = useState(headerNav);
    const [editableFooterNav, setEditableFooterNav] = useState(footerNav);
    const [isNavLoading, setIsNavLoading] = useState(false);

    useEffect(() => {
        setEditableHeaderNav(headerNav);
    }, [headerNav]);

    useEffect(() => {
        setEditableFooterNav(footerNav);
    }, [footerNav]);

    const findLink = (links: NavLink[], path: number[]): NavLink | null => {
        let current: NavLink | undefined = links[path[0]];
        for (let i = 1; i < path.length; i++) {
            current = current?.children?.[path[i]];
        }
        return current || null;
    };
    
    const findParentArray = (links: NavLink[], path: number[]): NavLink[] | null => {
        if (path.length === 1) return links;
        let parentPath = path.slice(0, -1);
        let current: NavLink | undefined = links[parentPath[0]];
        for (let i = 1; i < parentPath.length; i++) {
            current = current?.children?.[parentPath[i]];
        }
        return current?.children || null;
    };

    const handleUpdateNavLink = (navType: 'header' | 'footer', path: number[], field: keyof NavLink, value: any) => {
        const updater = navType === 'header' ? setEditableHeaderNav : setEditableFooterNav;
        updater(produce(draft => {
            if (!draft) return;
            const link = findLink(draft.links, path);
            if (link) {
                (link as any)[field] = value;
            }
        }));
    };

    const handleAddNavLink = (navType: 'header' | 'footer', parentPath?: number[]) => {
        const updater = navType === 'header' ? setEditableHeaderNav : setEditableFooterNav;
        updater(produce(draft => {
            if (!draft) return;
            const newLink: NavLink = { id: `new-${Date.now()}`, name: 'New Link', path: '/', target: '_self' };
            if (parentPath && parentPath.length > 0) {
                const parent = findLink(draft.links, parentPath);
                if (parent) {
                    if (!parent.children) parent.children = [];
                    parent.children.push(newLink);
                }
            } else {
                draft.links.push(newLink);
            }
        }));
    };

    const handleRemoveNavLink = (navType: 'header' | 'footer', path: number[]) => {
        const updater = navType === 'header' ? setEditableHeaderNav : setEditableFooterNav;
        updater(produce(draft => {
            if (!draft) return;
            const parentArray = findParentArray(draft.links, path);
            if (parentArray) {
                parentArray.splice(path[path.length - 1], 1);
            }
        }));
    };
    
    const handleMoveNavLink = (navType: 'header' | 'footer', path: number[], direction: 'up' | 'down') => {
        const updater = navType === 'header' ? setEditableHeaderNav : setEditableFooterNav;
        updater(produce(draft => {
            if (!draft) return;
            const parentArray = findParentArray(draft.links, path);
            const index = path[path.length - 1];

            if (!parentArray) return;

            if (direction === 'up' && index > 0) {
                [parentArray[index], parentArray[index - 1]] = [parentArray[index - 1], parentArray[index]];
            } else if (direction === 'down' && index < parentArray.length - 1) {
                [parentArray[index], parentArray[index + 1]] = [parentArray[index + 1], parentArray[index]];
            }
        }));
    };

    const handleSaveNavigation = async (navType: 'header' | 'footer') => {
        const navData = navType === 'header' ? editableHeaderNav : editableFooterNav;
        if (!navData) return;
        
        setIsNavLoading(true);
        try {
            await updateNavigation(navType === 'header' ? 'headerNav' : 'footerNav', navData);
            showToast(`${navType === 'header' ? 'Header' : 'Footer'} navigation saved!`);
        } catch (error) {
            showToast('Failed to save navigation.');
        } finally {
            setIsNavLoading(false);
        }
    };
    
    return (
        <div className="space-y-8">
            {/* Header Navigation */}
            <div>
                <h3 className="text-md font-semibold text-indigo-800 mb-2">Header Navigation</h3>
                <div className="space-y-2 p-4 bg-white rounded-lg border">
                    {editableHeaderNav?.links.map((link, index) => (
                        <NavLinkEditor
                            key={link.id || index}
                            link={link}
                            path={[index]}
                            navType="header"
                            index={index}
                            siblingsCount={editableHeaderNav.links.length}
                            onUpdate={handleUpdateNavLink}
                            onRemove={handleRemoveNavLink}
                            onAddChild={(navType, path) => handleAddNavLink(navType, path)}
                            onMove={handleMoveNavLink}
                        />
                    ))}
                    <button onClick={() => handleAddNavLink('header')} className="mt-2 text-sm text-indigo-600 font-semibold">+ Add Top-Level Link</button>
                </div>
                <button onClick={() => handleSaveNavigation('header')} disabled={isNavLoading} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md disabled:opacity-50">{isNavLoading ? 'Saving...' : 'Save Header Nav'}</button>
            </div>

            {/* Footer Navigation */}
            <div>
                 <h3 className="text-md font-semibold text-indigo-800 mb-2">Footer Navigation</h3>
                <div className="space-y-2 p-4 bg-white rounded-lg border">
                    {editableFooterNav?.links.map((link, index) => (
                         <NavLinkEditor
                            key={link.id || index}
                            link={link}
                            path={[index]}
                            navType="footer"
                            index={index}
                            siblingsCount={editableFooterNav.links.length}
                            onUpdate={handleUpdateNavLink}
                            onRemove={handleRemoveNavLink}
                            onAddChild={(navType, path) => handleAddNavLink(navType, path)}
                            onMove={handleMoveNavLink}
                        />
                    ))}
                    <button onClick={() => handleAddNavLink('footer')} className="mt-2 text-sm text-indigo-600 font-semibold">+ Add Top-Level Link</button>
                </div>
                <button onClick={() => handleSaveNavigation('footer')} disabled={isNavLoading} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md disabled:opacity-50">{isNavLoading ? 'Saving...' : 'Save Footer Nav'}</button>
            </div>
        </div>
    );
};